import { fetchWithAuth } from './api';

/**
 * Try multiple endpoints with the same request until one succeeds
 * @param endpoints Array of endpoints to try
 * @param options Fetch options
 * @returns Promise resolving to the successful response or error
 */
export async function tryMultipleEndpoints(
  endpoints: string[], 
  options?: RequestInit
): Promise<any> {
  let lastError = null;
  let lastErrorData = null;
  
  for (const endpoint of endpoints) {
    try {
      console.log(`Trying endpoint: ${endpoint}`);
      const response = await fetchWithAuth(endpoint, options);
      
      // Handle null or empty responses
      if (!response) {
        console.log(`Endpoint ${endpoint} returned null or empty response`);
        continue;
      }
      
      // For notification and user endpoints, accept any non-empty response
      if (endpoint.includes('notification') || endpoint.includes('user')) {
        console.log(`Success with endpoint: ${endpoint}`);
        return response;
      }
      
      // Check if response has expected shape or has a message/error field
      if (
        // These are the success cases - return immediately
        (Array.isArray(response)) || 
        (typeof response === 'object' && response !== null)
      ) {
        console.log(`Success with endpoint: ${endpoint}`);
        return response;
      } else if (response.message || response.error) {
        console.log(`Endpoint ${endpoint} returned a message: ${response.message || response.error}`);
        // Store the error data so we can return it if all endpoints fail
        lastErrorData = response;
      } else {
        console.log(`Endpoint ${endpoint} returned unexpected data structure:`, response);
        // Still consider it a success if it's any object with data
        if (typeof response === 'object' && response !== null) {
          return response;
        }
      }
    } catch (err) {
      console.log(`Failed with endpoint ${endpoint}:`, err);
      lastError = err;
      
      // Handle specific error cases
      if (err instanceof Error) {
        // Check for specific error messages from the backend
        if (err.message.includes("No valid recipients found")) {
          console.error("Error: The backend couldn't find valid recipients. Check if user IDs are correct.");
        } else if (err.message.includes("No User matches")) {
          // Try modifying the next endpoint to use username instead
          const username = localStorage.getItem('username');
          if (username && !endpoint.includes('username=')) {
            // Add an extra endpoint with username parameter
            endpoints.push(endpoint.replace('userId=', 'username=') + '&useUsername=true');
          }
        }
      }
    }
  }
  
  // If we have error data from an endpoint that returned a message, return that
  if (lastErrorData) {
    return lastErrorData;
  }
  
  // If we get here, all endpoints failed
  console.error("All endpoints failed:", lastError);
  throw lastError || new Error("All endpoints failed");
}